import React, { MouseEvent, FC, useState } from "react";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";

import { toDateString } from "~/libs";

import {
  useApisManagersEmployeeConditionSurveysCreate,
  useApisManagersEmployeeConditionSurveysNew,
  useBoolean,
  useDropdown,
  useInput,
  useMultiDivisionMultiSectionDropdown,
  useProvidersCurrentEmployee,
} from "~/hooks";

import { Button } from "~/components/atoms";
import {
  EmployeeLayout,
  EmployeeLayoutMainContent,
  EmployeeLayoutRightSidebar,
} from "~/components/layouts";
import {
  DatePicker,
  LabelWithDropDownField,
  LabelWithTextArea,
  LabelWithTextField,
} from "~/components/molecules";
import { AllDistributionCheckboxMultiParentsWithMultiChildrenDropdownField } from "~/components/organisms";

import { PublicStatus } from "~/domains";

export const ManagersEmployeeConditionSurveysNew: FC = () => {
  const navigate = useNavigate();

  const { currentEmployee } = useProvidersCurrentEmployee();
  const { mutate: createRequest, isLoading: isSubmitting } =
    useApisManagersEmployeeConditionSurveysCreate();
  const { data: surveyTemplates } =
    useApisManagersEmployeeConditionSurveysNew();
  const [{ value: title, onChange: onChangeTitle }] = useInput("");
  const [{ value: description, onChange: onChangeDescription }] = useInput("");
  const [dropdownAnswerShareOption] = useDropdown();
  const [dropdownSurveyTemplate] = useDropdown();
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const {
    isChecked: allBusinessSectionsDistribution,
    handleChange: onChangeAllBusinessSectionsDistribution,
  } = useBoolean(false);

  const {
    selectableDivisions,
    selectedDivisions,
    onDivisionChange,
    optionSelectableSections,
    optionSelectedSections,
    onSectionChange,
  } = useMultiDivisionMultiSectionDropdown({
    selectableDivisions: currentEmployee?.businessDivisions,
    selectableSections: currentEmployee?.businessSections,
  });

  const breadCrumbItems = [
    { label: "コンディション", href: "/managers/employee_condition_surveys" },
    { label: "新規作成", href: "/managers/employee_condition_surveys/new" },
  ];

  const handleSubmit =
    (publicStatusId: number) => (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();

      const body = {
        title,
        description,
        startDate: startDate ? toDateString(startDate) : "",
        endDate: endDate ? toDateString(endDate) : "",
        answerShareOptionId: parseInt(
          dropdownAnswerShareOption.dropdownValue?.value || "1",
        ),
        selectedSurveyTemplateId: parseInt(
          dropdownSurveyTemplate.dropdownValue?.value || "1",
        ),
        allBusinessSectionsDistribution,
        businessSectionIds: optionSelectedSections.map((section) => section.id),
        publicStatusId,
      };

      createRequest(
        { employeeConditionSurvey: body },
        {
          onSuccess: (data) => {
            toast(data.message);
            navigate(
              `/managers/employee_condition_surveys/${data.employeeConditionSurvey.id}/edit`,
            );
          },
        },
      );
    };

  return (
    <EmployeeLayout withRightSidebar breadCrumbItems={breadCrumbItems}>
      <EmployeeLayoutMainContent withRightSidebar>
        <div className="space-y-6">
          <AllDistributionCheckboxMultiParentsWithMultiChildrenDropdownField
            allParents={selectableDivisions}
            parentsValue={selectedDivisions}
            parentsOnChange={onDivisionChange}
            parentRequired={true}
            parentLabel="配布先部署"
            allChildren={optionSelectableSections}
            childrenValue={optionSelectedSections}
            childrenOnChange={onSectionChange}
            childRequired={true}
            childLabel="配布先課"
            allBusinessSectionsDistribution={allBusinessSectionsDistribution}
            onChangeAllBusinessSectionsDistribution={
              onChangeAllBusinessSectionsDistribution
            }
            currentEmployeeRole={currentEmployee?.employeeRole}
          />
          <LabelWithTextField
            labelText="タイトル"
            type="text"
            name="title"
            placeholder="タイトル"
            required
            value={title}
            onChange={onChangeTitle}
          />
          <LabelWithTextArea
            labelText="説明"
            name="description"
            placeholder="説明"
            required
            value={description}
            onChange={onChangeDescription}
          />
          <LabelWithDropDownField
            labelText="質問テンプレート選択"
            required
            name="dropdownSurveyTemplate"
            options={surveyTemplates?.surveyTemplates.map((template) => ({
              value: template.id.toString(),
              label: template.name,
            }))}
            value={dropdownSurveyTemplate.dropdownValue}
            onChange={dropdownSurveyTemplate.onChange}
          />
          <LabelWithDropDownField
            labelText="共有方法の選択"
            required
            name="dropdownAnswerShareOption"
            options={surveyTemplates?.answerShareOptions.map((status) => ({
              value: status.id.toString(),
              label: status.name,
            }))}
            value={dropdownAnswerShareOption.dropdownValue}
            onChange={dropdownAnswerShareOption.onChange}
          />
          <DatePicker
            labelText="開始日"
            selectDate={startDate}
            onChange={setStartDate}
            parentClassName="mt-6"
            withoutButton
            required
            textLeft
          />
          <DatePicker
            labelText="終了日"
            selectDate={endDate}
            onChange={setEndDate}
            parentClassName="mt-6"
            withoutButton
            textLeft
          />
          <div className="flex gap-4">
            <Button
              color="gray"
              className="flex-1"
              readonly={isSubmitting}
              onClick={handleSubmit(PublicStatus.DRAFTED.id)}
              text="下書き保存"
            />
            <Button
              color="primary"
              className="flex-1"
              readonly={isSubmitting}
              onClick={handleSubmit(PublicStatus.PUBLISHED.id)}
              text="配布する"
            />
          </div>
        </div>
      </EmployeeLayoutMainContent>
      <EmployeeLayoutRightSidebar />
    </EmployeeLayout>
  );
};
